import { Box, Card, CardContent, Grid2, Typography } from '@mui/material'
import { Constants, Page_Padding, ImagePath, ImageRelativePath } from '../../shared/constants'
import CustomDivider from '../../shared/utils/CustomDivider'
import { Icon } from '@iconify/react/dist/iconify.js'
import { useMemo, useState, useEffect } from 'react'
import ActivityLMSCourseCard from '../../shared/components/ActivityLMSCourseCard.tsx'
import useResponsive from '../../shared/utils/Responsive'
import { Activitylms } from '../../assets/api/useGetApi'
import { SkeletonCom } from '../../shared/utils/SkeletonCom'
import SkillBoxSearchBar from './SkillBoxSearchBar'
import { usePageContentWithMeta } from '../../shared/hook/usePageContentWithMeta';
import Loader from '../../shared/utils/Loader';
import { getImageUrl } from '../../shared/utils/getImageUrl';
import { indexedDBStorage } from '../../shared/utils/indexedDB/storage.js'

const SkillBoxColors = {
    blue: "#87CEEB"
}
const SkillBoxComponent = ({ img, heading, description, bgcolor }) => {

    return (
        <Card sx={{ boxShadow: "none", width: "100%", height: "100%", display: "flex", flexDirection: "column", justifyContent: "start", background: "transparent" }}>
            <CardContent
                sx={{
                    backgroundImage: `url("${img}")`,
                    backgroundSize: "cover",
                    backgroundPosition: "start",
                    height: "200px",
                    borderTopRightRadius: "15px",
                    borderTopLeftRadius: "15px",
                }}
            />
            <CardContent sx={{
                height: "auto", bgcolor: bgcolor,
                borderBottomRightRadius: "15px",
                borderBottomLeftRadius: "15px",
                p: "0.5rem",
            }}>
                <Typography variant="body1" color='primary.main' sx={{ padding: "0.5rem" }} dangerouslySetInnerHTML={{ __html: heading }} />
            </CardContent>
            <CardContent sx={{ width: "100%", mt: "1rem" }}>
                <Typography variant="body2" fontWeight={200} dangerouslySetInnerHTML={{ __html: description }} />
            </CardContent>
        </Card>
    )
}


const SkillBox = () => {
    const isMediumScreen = useResponsive("md");

    // Fetch real SkillBox content from the API
    const { data: skillBoxCMSData, isLoading: isSkillBoxCMSLoading } = usePageContentWithMeta("SKILL_BOX");

    const bannerSection = skillBoxCMSData?.content?.find(item => item.type === "banner")?.data || {};
    const foundationSection1 = skillBoxCMSData?.content?.find(item => item.type === "segment" && item.position === 1)?.data || {};
    const foundationSection2 = skillBoxCMSData?.content?.find(item => item.type === "segment" && item.position === 2)?.data || {};
    const benefitsSection = skillBoxCMSData?.content?.find(item => item.type === "segment" && item.position === 3)?.data || {};
    const trainingSection = skillBoxCMSData?.content?.find(item => item.type === "segment" && item.position === 4)?.data || {};
    const benefitsForPreschoolsSection = skillBoxCMSData?.content?.find(item => item.type === "segment" && item.position === 5)?.data || {};
    const approachSection = skillBoxCMSData?.content?.find(item => item.type === "metadata")?.data || {};
    const ctaSection = skillBoxCMSData?.content?.find(item => item.type === "cta")?.data || {};

    const { data: activityLmsData, isLoading: isActivityLmsLoading } = Activitylms();

    useEffect(() => {
        if (activityLmsData?.courses) {
            sessionStorage.setItem("activityLmsData", JSON.stringify(activityLmsData.courses));
            indexedDBStorage.set("activityLmsData", activityLmsData.courses);
        }
    }, [activityLmsData]);

    const courseDetails = activityLmsData?.courses || [];

    const skillBoxProducts = courseDetails
        .filter((details) => details.price != null && details.cover_image != null)
        .map((details) => {
            return {
                ...details,
                title: details.title || details.course_name,
                category: details.category || details.grade_name,
                price: details.price,
                description: details.description,
                image: `${ImagePath}${ImageRelativePath}${details.cover_image}`,
            };
        });

    const isSkillBoxLoading = isActivityLmsLoading;

    const [searchKeyword, setSearchKeyword] = useState("");

    const productList = useMemo(() => {
        if (!searchKeyword?.trim()) return skillBoxProducts;
        return skillBoxProducts.filter((item) =>
            item?.title?.toLowerCase()?.includes(searchKeyword?.trim()?.toLowerCase())
        );
    }, [skillBoxProducts, searchKeyword]);

    if (isSkillBoxCMSLoading) return <Loader />;

    return (
        <Box className="body contact_us" px={Page_Padding} sx={{ maxWidth: '1536px', mx: 'auto' }}>

            {/* ── Hero Section ── */}
            <Grid2
                container
                spacing={"1rem"}
                sx={{ position: "relative", minHeight: { xs: "auto", md: "420px" }, alignItems: "center" }}
            >
                {/* Decorative Rings – position & size respond to screen size */}

                {/* Ring 1 – orange, top-right diagonal */}
                <Box sx={{
                    display: { xs: "none", sm: "block" },
                    position: "absolute",
                    width: { sm: "22px", md: "30px", lg: "36px" },
                    height: { sm: "22px", md: "30px", lg: "36px" },
                    borderRadius: "50%",
                    border: "3px solid #FAC584",
                    top: { sm: "14px", md: "20px", lg: "20px" },
                    right: { sm: "70px", md: "28%", lg: "30%" },
                    zIndex: 0,
                }} />

                {/* Ring 2 – blue, mid diagonal */}
                <Box sx={{
                    display: { xs: "none", sm: "block" },
                    position: "absolute",
                    width: { sm: "22px", md: "30px", lg: "36px" },
                    height: { sm: "22px", md: "30px", lg: "36px" },
                    borderRadius: "50%",
                    border: "3px solid #87CEEB",
                    top: { sm: "56px", md: "72px", lg: "75px" },
                    right: { sm: "40px", md: "40%", lg: "42%" },
                    zIndex: 0,
                }} />

                {/* Ring 3 – orange, lower diagonal */}
                <Box sx={{
                    display: { xs: "none", sm: "block" },
                    position: "absolute",
                    width: { sm: "22px", md: "30px", lg: "36px" },
                    height: { sm: "22px", md: "30px", lg: "36px" },
                    borderRadius: "50%",
                    border: "3px solid #FAC584",
                    top: { sm: "100px", md: "124px", lg: "130px" },
                    right: { sm: "10px", md: "52%", lg: "54%" },
                    zIndex: 0,
                }} />

                {/* Triangle accent – blue */}
                <Box
                    sx={{
                        display: { xs: "none", sm: "block" },
                        position: "absolute",
                        width: { sm: "24px", md: "30px", lg: "36px" },
                        height: { sm: "24px", md: "30px", lg: "36px" },
                        backgroundColor: "#87CEEB",
                        top: { sm: "36px", md: "44px", lg: "50px" },
                        right: { sm: "6px", md: "1%", lg: "2%" },
                        zIndex: 2,
                        transform: "rotate(160deg)",
                        clipPath: "polygon(100% 100%, 0% 100%, 50% 0%)"
                    }}
                />

                {/* Text Content */}
                <Grid2
                    container
                    size={{ xs: 12, md: 7.3 }}
                    sx={{
                        zIndex: 1,
                        pl: { md: "2.5rem" },
                        px: { xs: "0.5rem", sm: "1rem", md: "0" },
                        gap: "0.75rem",
                        flexDirection: "column",
                        marginY: { xs: 3, md: 6 }
                    }}
                >
                    {/* "About Us" Badge */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, position: "relative", width: "fit-content" }}>
                        <Box sx={{
                            position: 'absolute',
                            bgcolor: SkillBoxColors.blue,
                            width: "1.8rem",
                            height: "1.8rem",
                            borderRadius: 50,
                            left: 0,
                            zIndex: 0
                        }} />
                        <Typography
                            variant='body2'
                            fontWeight={500}
                            sx={{ pl: "0.5rem", zIndex: 1, '& p, & span': { m: 0 } }}
                            dangerouslySetInnerHTML={{ __html: bannerSection.heading }}
                        />
                    </Box>

                    {/* Heading – smaller on mobile */}
                    <Typography
                        variant="h3"
                        sx={{ '& h3, & p': { m: 0, fontSize: "inherit", color: "inherit", fontWeight: "inherit" } }}
                        dangerouslySetInnerHTML={{ __html: bannerSection.subheading }}
                    />

                    {/* Paragraphs */}
                    <Typography
                        variant="body2"
                        color='grey.400'
                        sx={{ '& p': { m: 0 } }}
                        dangerouslySetInnerHTML={{ __html: bannerSection.description || "" }}
                    />
                    <Typography
                        variant="body2"
                        color='grey.400'
                        sx={{ '& p': { m: 0 } }}
                        dangerouslySetInnerHTML={{ __html: bannerSection.description2 || "" }}
                    />
                </Grid2>

                {/* Hero Image */}
                <Grid2
                    size={{ xs: 12, md: 4.7 }}
                    sx={{
                        zIndex: 1,
                        width: '100%',
                        backgroundImage: `url(${getImageUrl(bannerSection.imageUrl || Constants.customImages.SkillBoxHeroImg)})`,
                        backgroundSize: 'contain',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat',
                        aspectRatio: '16/9',
                        minHeight: { xs: "180px", sm: "220px", md: "300px" },
                        mt: { md: "28px", xs: "8px" }
                    }}
                />
            </Grid2>


            {/* ── Why MySkill Box ── */}
            <Box
                sx={{
                    width: "100%",
                    backgroundImage: `url(${Constants.customImages.SkillBoxHeadingImg})`,
                    backgroundSize: "contain",
                    backgroundPosition: "start",
                    backgroundRepeat: "no-repeat",
                    pt: { xs: "2rem", sm: "3rem", md: "5rem" },
                }}
            >
                <Typography
                    variant="body2"
                    color="red.main"
                    textAlign={"center"}
                    mb={1}
                    sx={{ '& p': { m: 0 } }}
                    dangerouslySetInnerHTML={{ __html: foundationSection1.heading }}
                />

                <Typography
                    variant="h3"
                    textAlign={"center"}
                    mb={3}
                    dangerouslySetInnerHTML={{ __html: foundationSection1.subheading }}
                />

                <Typography
                    variant="body2"
                    color="grey.400"
                    textAlign={"center"}
                    mx={{ xs: 1, sm: 4, md: 8 }}
                    mb={3}
                    dangerouslySetInnerHTML={{ __html: foundationSection1.description }}
                />

                <Box
                    sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        justifyContent: "center",
                        alignItems: "center",
                        gap: { xs: 1, sm: 2 },
                        maxWidth: "1200px",
                        mx: "auto",
                        px: { xs: 1, sm: 0 },
                    }}
                >
                    {(foundationSection1.features).map((feature) => (
                        <Box
                            key={feature}
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: { xs: "flex-start", sm: "center" },
                                gap: 1,
                                px: { xs: 1, sm: 2 },
                                width: {
                                    xs: "100%",
                                    sm: "45%",
                                    md: "30%",
                                },
                                textAlign: { xs: "left", sm: "center" },
                            }}
                        >
                            <Icon
                                icon="material-symbols:star"
                                width="16"
                                height="16"
                                color="red.main"
                                style={{ flexShrink: 0 }}
                            />

                            <Typography variant="body2" color="grey.400">
                                {feature}
                            </Typography>
                        </Box>
                    ))}
                </Box>

                <Typography
                    variant="body2"
                    color="grey.400"
                    textAlign={"center"}
                    mx={{ xs: 1, sm: 4 }}
                    mt={3}
                    sx={{ '& p': { m: 0 } }}
                    dangerouslySetInnerHTML={{ __html: foundationSection1.footerText || "" }}
                />
            </Box>

            <CustomDivider />

            {/* ── PRESKOOL ── */}
            <Box
                sx={{
                    width: "100%",
                    position: "relative",
                    pt: { xs: "1.5rem", md: "2rem" },
                }}
            >
                {/* Decorative Rings */}
                <Box sx={{
                    display: { xs: "none", sm: "block" },
                    position: "absolute", width: "36px", height: "36px", borderRadius: "50%",
                    border: "3px solid #FAC584", top: "5px", right: { sm: "10px", md: "10%" }, zIndex: 0
                }} />
                <Box sx={{
                    display: { xs: "none", sm: "block" },
                    position: "absolute", width: "36px", height: "36px", borderRadius: "50%",
                    border: "3px solid #87CEEB", top: "50px", left: { sm: "5%", md: "5%" }, zIndex: 0
                }} />
                <Typography
                    variant="body2"
                    color="red.main"
                    textAlign={"center"}
                    mb={1}
                    dangerouslySetInnerHTML={{ __html: foundationSection2.heading }}
                />

                <Typography
                    variant="h3"
                    textAlign={"center"}
                    mb={2}
                    dangerouslySetInnerHTML={{ __html: foundationSection2.subheading }}
                />

                <Typography
                    variant="body2"
                    color="grey.400"
                    textAlign={"center"}
                    mx={{ xs: 1, sm: 4, md: 16 }}
                    mb={1}
                    dangerouslySetInnerHTML={{ __html: foundationSection2.description }}
                />

                <Grid2
                    container
                    spacing={"1rem"}
                    sx={{ alignItems: "center", mt: { md: "32px", xs: "12px" } }}
                >
                    {/* Images - LEFT on desktop, TOP on mobile */}
                    <Grid2
                        container
                        size={{ xs: 12, md: 7.5 }}
                    >
                        {/* Images Grid */}
                        <Grid2
                            container
                            sx={{
                                position: "relative",
                                width: "100%",
                                minHeight: { xs: "260px", sm: "320px", md: "400px" },
                            }}
                        >
                            <Grid2 size={5}>
                                <Box
                                    component="img"
                                    src={getImageUrl(foundationSection2.imageUrl1)}
                                    alt="Classroom activity"
                                    sx={{
                                        width: '100%',
                                        height: { xs: "260px", sm: "360px", md: "420px" },
                                        objectFit: "fill",
                                        borderRadius: "12px",
                                    }}
                                />
                            </Grid2>
                            <Grid2 size={7} sx={{ position: "relative" }}>
                                <Box
                                    component="img"
                                    src={getImageUrl(foundationSection2.imageUrl2)}
                                    alt="Children Playing"
                                    sx={{
                                        position: "absolute",
                                        top: 0,
                                        right: { xs: 0, md: "20px" },
                                        width: { xs: "90%", sm: "90%", md: "90%" },
                                        height: { xs: "140px", sm: "170px", md: "200px" },
                                        objectFit: "fill",
                                        borderRadius: "12px",
                                        zIndex: 2,
                                    }}
                                />
                                {/* Decorative circles */}
                                <Box sx={{
                                    display: { xs: "none", sm: "block" },
                                    position: "absolute", width: "28px", height: "28px", borderRadius: "50%",
                                    border: "3px solid #87CEEB", top: "50%", left: "4%", zIndex: 21
                                }} />
                                <Box sx={{
                                    display: { xs: "none", sm: "block" },
                                    position: "absolute", width: "28px", height: "28px", borderRadius: "50%",
                                    border: "3px solid #FAC584", bottom: "4px", left: "12%", zIndex: 20
                                }} />
                                {/* Bottom-Right image */}
                                <Box
                                    component="img"
                                    src={getImageUrl(foundationSection2.imageUrl3)}
                                    alt="Skill Box"
                                    sx={{
                                        position: "absolute",
                                        bottom: 0,
                                        right: { xs: 0, md: "20px" },
                                        width: { xs: "80%", sm: "75%", md: "70%" },
                                        height: { xs: "120px", sm: "155px", md: "185px" },
                                        objectFit: "fill",
                                        borderRadius: "12px",
                                        zIndex: 1,
                                    }}
                                />
                            </Grid2>
                        </Grid2>
                    </Grid2>

                    {/* Text - RIGHT on desktop, BELOW on mobile */}
                    <Grid2
                        size={{ xs: 12, md: 4.5 }}
                        sx={{ width: '100%', mt: { xs: "1rem", md: 0 } }}
                    >
                        {(foundationSection2.points || []).map((point, idx) => (
                            <Box key={idx} mb={idx < (foundationSection2.points.length - 1) ? 2 : 0}>
                                <Typography
                                    variant="body1"
                                    fontWeight={600}
                                    mb={1}
                                    dangerouslySetInnerHTML={{ __html: point.heading }}
                                />
                                <Typography
                                    variant="body2"
                                    color="grey.300"
                                    dangerouslySetInnerHTML={{ __html: point.text }}
                                />
                            </Box>
                        ))}
                    </Grid2>

                </Grid2>

            </Box>


            <Box sx={{ mt: { md: "46px", xs: "24px" } }}>
                <Grid2
                    container
                    spacing={"1rem"}
                    sx={{ alignItems: "center" }}
                >
                    {/* Text – shown BELOW images on mobile, LEFT on desktop */}
                    <Grid2
                        size={{ xs: 12, md: 4.5 }}
                        sx={{ width: '100%', order: { xs: 2, md: 1 } }}
                    >
                        {(benefitsSection.points || []).map((point, idx) => {
                            const isSecond = idx === 1;
                            return (
                                <Box key={idx} mb={idx < (benefitsSection.points.length - 1) ? 2 : 0}>
                                    <Typography
                                        variant="body1"
                                        fontWeight={600}
                                        mb={1}
                                        dangerouslySetInnerHTML={{ __html: point.heading }}
                                    />
                                    {isSecond ? (
                                        <Box sx={{ position: "relative" }}>
                                            <Box sx={{
                                                display: { xs: "none", sm: "block" },
                                                position: "absolute", width: "36px", height: "36px", borderRadius: "50%",
                                                border: "3px solid #FAC584", right: "1%", zIndex: 20
                                            }} />
                                            <Typography
                                                variant="body2"
                                                color="grey.300"
                                                dangerouslySetInnerHTML={{ __html: point.text }}
                                            />
                                        </Box>
                                    ) : (
                                        <Typography
                                            variant="body2"
                                            color="grey.300"
                                            dangerouslySetInnerHTML={{ __html: point.text }}
                                        />
                                    )}
                                </Box>
                            );
                        })}
                    </Grid2>

                    {/* Images – shown ABOVE on mobile, RIGHT on desktop */}
                    <Grid2
                        container
                        size={{ xs: 12, md: 7.5 }}
                        sx={{ order: { xs: 1, md: 2 } }}
                    >
                        <Grid2
                            container
                            sx={{
                                position: "relative",
                                width: "100%",
                                minHeight: { xs: "280px", sm: "340px", md: "420px" },
                            }}
                        >
                            <Grid2 size={7} sx={{ position: "relative" }}>
                                <Box
                                    component="img"
                                    src={getImageUrl(benefitsSection.imageUrl1)}
                                    alt="Children Playing"
                                    sx={{
                                        position: "absolute",
                                        top: 0,
                                        right: { xs: 0, md: "10px" },
                                        width: { xs: "75%", sm: "70%", md: "70%" },
                                        height: { xs: "130px", sm: "160px", md: "200px" },
                                        objectFit: "fill",
                                        borderRadius: "12px",
                                        zIndex: 2,
                                    }}
                                />
                                <Box sx={{
                                    display: { xs: "none", sm: "block" },
                                    position: "absolute", width: "28px", height: "28px", borderRadius: "50%",
                                    border: "3px solid #87CEEB", top: "40%", left: "6%", zIndex: 20
                                }} />
                                <Box
                                    component="img"
                                    src={getImageUrl(benefitsSection.imageUrl2)}
                                    alt="Skill Box"
                                    sx={{
                                        position: "absolute",
                                        bottom: 10,
                                        left: { xs: 0, md: "10px" },
                                        width: { xs: "95%", sm: "95%", md: "95%" },
                                        height: { xs: "130px", sm: "160px", md: "200px" },
                                        objectFit: "fill",
                                        borderRadius: "12px",
                                    }}
                                />
                            </Grid2>
                            <Grid2 size={5}>
                                <Box
                                    component="img"
                                    src={getImageUrl(benefitsSection.imageUrl3)}
                                    alt="Classroom activity"
                                    sx={{
                                        width: '100%',
                                        height: { xs: "280px", sm: "340px", md: "420px" },
                                        objectFit: "fill",
                                        borderRadius: "12px",
                                    }}
                                />
                            </Grid2>
                        </Grid2>
                    </Grid2>

                </Grid2>
            </Box>

            <Box
                sx={{
                    width: "100%",
                    pt: "1rem",
                }}
            >
                <Grid2
                    container
                    spacing={"1rem"}
                    sx={{ alignItems: "stretch", mt: { md: "32px", xs: "12px" } }}
                >
                    {/* Image - LEFT SIDE */}
                    <Grid2
                        size={{ xs: 12, md: 6 }}
                        sx={{ display: 'flex' }}
                    >
                        <Box
                            component="img"
                            src={getImageUrl(trainingSection.imageUrl)}
                            alt="Skill Box"
                            sx={{
                                width: "100%",
                                objectFit: "fill",
                                borderRadius: "12px",
                            }}
                        />
                    </Grid2>

                    <Grid2
                        size={{ xs: 12, md: 5 }}
                        sx={{
                            width: '100%',
                            mx: "auto",
                        }}
                    >
                        {(trainingSection.points || []).map((point, idx) => (
                            <Box key={idx} mb={idx < (trainingSection.points.length - 1) ? 2 : 0}>
                                <Typography
                                    variant="body1"
                                    fontWeight={600}
                                    mb={1}
                                    dangerouslySetInnerHTML={{ __html: point.heading }}
                                />
                                {idx === 0 ? (
                                    <Box sx={{ position: "relative", width: "100%" }}>
                                        <Box sx={{
                                            position: "absolute", width: "36px", height: "36px", borderRadius: "50%",
                                            border: "3px solid #FAC584", top: "4%", right: "-12%", zIndex: 20
                                        }} />
                                        <Typography
                                            variant="body2"
                                            color="grey.300"
                                            dangerouslySetInnerHTML={{ __html: point.text }}
                                        />
                                    </Box>
                                ) : (
                                    <Typography
                                        variant="body2"
                                        color="grey.300"
                                        dangerouslySetInnerHTML={{ __html: point.text }}
                                    />
                                )}
                            </Box>
                        ))}
                    </Grid2>

                </Grid2>

            </Box>

            {/* ── Product List ── */}
            <Box
                sx={{
                    width: "100%",
                    backgroundImage: `url(${Constants.customImages.SkillBoxHeadingImg})`,
                    backgroundSize: "contain",
                    backgroundPosition: "start",
                    backgroundRepeat: "no-repeat",
                    pt: "3rem",
                }}
            >
                <Typography variant="body2"
                    color="red.main"
                    textAlign={"center"}
                >Our Learning Resources</Typography>
                <Typography variant='h3' textAlign={"center"}>
                    Course List
                </Typography>
                <CustomDivider size={"2rem"} />
                <Grid2 container spacing={"1rem"} >
                    <Grid2 size={{ xs: 12, sm: 6, md: 6 }}>
                        <SkillBoxSearchBar
                            options={skillBoxProducts.map((c) => ({
                                id: c?.title,
                                label: c?.title,
                            }))}
                            onSearch={(keyword) => setSearchKeyword(keyword || "")}
                            placeholder="Search for Courses..."
                        />
                    </Grid2>
                </Grid2>

                <Grid2 container spacing={{ xs: "1rem", md: "2rem" }} sx={{ paddingTop: "20px" }}>
                    {isSkillBoxLoading &&
                        [...Array(4)].map((_, index) => (
                            <Grid2 size={{ xs: 6, sm: 4, md: 3, lg: 3 }} key={index}>
                                <SkeletonCom />
                            </Grid2>
                        ))
                    }
                    {
                        productList.map((item, index) => (
                            <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 4 }} key={item?.slug || index}>
                                <ActivityLMSCourseCard item={item} />
                            </Grid2>
                        ))
                    }
                    {
                        productList.length === 0 && (
                            <Grid2
                                size={{ xs: 12, sm: 12, md: 12 }}
                                sx={{
                                    textAlign: "center",
                                    marginTop: "24px",
                                    py: 4,
                                }}
                            >
                                <Icon
                                    icon="hugeicons:course"
                                    width="62"
                                    height="62"
                                    color="#afafaf"
                                />

                                <Typography variant="h6" sx={{ mt: 1, fontWeight: 600 }}>
                                    No Course Found
                                </Typography>

                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                    There are currently no course available. Please check back later.
                                </Typography>
                            </Grid2>
                        )
                    }

                </Grid2>

            </Box>
            <CustomDivider />

            {/* ── Benefits ── */}
            <Box sx={{
                width: "100%",
                backgroundImage: `url(${Constants.customImages.SkillBoxHeadingImg})`,
                backgroundSize: "contain",
                backgroundPosition: "start",
                backgroundRepeat: "no-repeat",
                pt: "3rem",
                pb: "2rem"

            }}>
                <Typography
                    variant='h3'
                    textAlign={"center"}
                    mb={1}
                    dangerouslySetInnerHTML={{ __html: benefitsForPreschoolsSection.heading }}
                />

                <Typography
                    variant="body2"
                    color="grey.400"
                    textAlign={"center"}
                    mx={{ xs: 1, sm: 4, md: 16 }}
                    mb={1}
                    dangerouslySetInnerHTML={{ __html: benefitsForPreschoolsSection.description }}
                />
                <CustomDivider size={"2rem"} />
                <Grid2 container spacing={"1rem"} sx={{ position: "relative" }}>
                    {!isMediumScreen && (
                        <Box sx={{
                            width: "100%",
                            height: "100%",
                            position: "absolute",
                            backgroundImage: `url(${Constants.customImages.SkillBoxConnectingWire})`,
                            backgroundSize: '70%',
                            backgroundPosition: 'center',
                            backgroundRepeat: 'no-repeat',
                            top: 0,
                            left: 0
                        }} />
                    )}

                    {/* Left Benefits Panel */}
                    <Grid2 container size={{ xs: 12, md: 6 }}
                        bgcolor={"#E2F3FD"}
                        sx={{ borderRadius: "1rem", p: "2rem", overflow: "hidden", pb: { xs: "auto", md: 0 } }}
                    >
                        <Grid2 size={{ xs: 12, sm: 6 }} zIndex={100}>
                            <SkillBoxComponent
                                img={getImageUrl(benefitsForPreschoolsSection.benefits?.[0]?.imageUrl)}
                                heading={benefitsForPreschoolsSection.benefits?.[0]?.heading}
                                description={benefitsForPreschoolsSection.benefits?.[0]?.description}
                                bgcolor={benefitsForPreschoolsSection.benefits?.[0]?.bgcolor}
                            />
                        </Grid2>
                        <Grid2 size={6} sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                            <Box component={"img"} src={Constants.customImages.SkillBoxFrame2} width={"50%"} />
                        </Grid2>
                        <Grid2 size={6} sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                            <Box component={"img"} src={Constants.customImages.SkillBoxFrame1} width={"50%"} />
                        </Grid2>
                        <Grid2 size={{ xs: 12, sm: 6 }}
                            sx={{ transform: isMediumScreen ? 'translateY(0px)' : 'translateY(-50px)' }}
                        >
                            <SkillBoxComponent
                                img={getImageUrl(benefitsForPreschoolsSection.benefits?.[1]?.imageUrl)}
                                heading={benefitsForPreschoolsSection.benefits?.[1]?.heading}
                                description={benefitsForPreschoolsSection.benefits?.[1]?.description}
                                bgcolor={benefitsForPreschoolsSection.benefits?.[1]?.bgcolor}
                            />
                        </Grid2>
                    </Grid2>

                    {/* Right Benefits Panel */}
                    <Grid2 container size={{ xs: 12, md: 6 }}
                        bgcolor={"#FBF1E8"}
                        sx={{ borderRadius: "1rem", p: "2rem", overflow: "hidden", pb: { xs: "auto", md: 0 } }}
                    >
                        <Grid2 size={6} sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                            <Box component={"img"} src={Constants.customImages.SkillBoxFrame1} width={"50%"} />
                        </Grid2>
                        <Grid2 size={{ xs: 12, sm: 6 }} zIndex={100}>
                            <SkillBoxComponent
                                img={getImageUrl(benefitsForPreschoolsSection.benefits?.[3]?.imageUrl)}
                                heading={benefitsForPreschoolsSection.benefits?.[3]?.heading}
                                description={benefitsForPreschoolsSection.benefits?.[3]?.description}
                                bgcolor={benefitsForPreschoolsSection.benefits?.[3]?.bgcolor || "#E6AA05"}
                            />
                        </Grid2>
                        <Grid2 size={{ xs: 12, sm: 6 }}
                            sx={{ transform: isMediumScreen ? 'translateY(0px)' : 'translateY(-50px)' }}
                        >
                            <SkillBoxComponent
                                img={getImageUrl(benefitsForPreschoolsSection.benefits?.[2]?.imageUrl)}
                                heading={benefitsForPreschoolsSection.benefits?.[2]?.heading}
                                description={benefitsForPreschoolsSection.benefits?.[2]?.description}
                                bgcolor={benefitsForPreschoolsSection.benefits?.[2]?.bgcolor}
                            />
                        </Grid2>
                        <Grid2 size={6} sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                            <Box
                                component={"img"}
                                src={Constants.customImages.SkillBoxFrame2}
                                sx={{
                                    width: "50%",
                                    maxWidth: "100%",
                                    height: "auto",
                                    objectFit: "fill",
                                    display: "block",
                                }}
                            />
                        </Grid2>
                    </Grid2>
                </Grid2>
            </Box>


            <Box
                sx={{
                    width: "100%",
                    backgroundImage: `url(${Constants.customImages.SkillBoxHeadingImg})`,
                    backgroundSize: "contain",
                    backgroundPosition: "start",
                    backgroundRepeat: "no-repeat",
                    pt: { xs: "2rem", md: "5rem" },
                }}
            >
                <Grid2
                    container
                    spacing={"1rem"}
                    sx={{ alignItems: { xs: "flex-start", md: "stretch" }, mt: { md: "32px", xs: "12px" } }}
                >
                    {/* Image */}
                    <Grid2
                        size={{ xs: 12, md: 6 }}
                        sx={{ display: 'flex' }}
                    >
                        <Box
                            component="img"
                            src={getImageUrl(approachSection.imageUrl || Constants.customImages.CarouselImg3)}
                            alt="Skill Box"
                            sx={{
                                width: "100%",
                                height: { xs: "220px", sm: "90%", md: "85%" },
                                objectFit: "fill",
                                borderRadius: "12px",
                            }}
                        />
                    </Grid2>

                    {/* Text */}
                    <Grid2
                        size={{ xs: 12, md: 5 }}
                        sx={{ width: '100%', mx: "auto" }}
                    >
                        <Box mb={2}>
                            <Typography
                                variant="body2"
                                color='red.main'
                                mb={1}
                                dangerouslySetInnerHTML={{ __html: approachSection.heading }}
                            />
                            <Typography
                                variant="h3"
                                mb={1.5}
                                dangerouslySetInnerHTML={{ __html: approachSection.subheading }}
                            />
                            <Box sx={{ position: "relative", width: "100%" }}>
                                <Box sx={{
                                    display: { xs: "none", sm: "block" },
                                    position: "absolute", width: "36px", height: "36px", borderRadius: "50%",
                                    border: "3px solid #FAC584", top: "4%", right: "-12%", zIndex: 20
                                }} />
                                <Typography
                                    variant="body2"
                                    color="grey.300"
                                    mb={1.5}
                                    dangerouslySetInnerHTML={{ __html: approachSection.description }}
                                />
                                <Typography
                                    variant="body2"
                                    color="grey.300"
                                    mb={1}
                                    dangerouslySetInnerHTML={{ __html: approachSection.description2 }}
                                />
                                <Typography
                                    variant="body2"
                                    color="grey.300"
                                    dangerouslySetInnerHTML={{ __html: approachSection.footerText }}
                                />
                            </Box>
                        </Box>
                    </Grid2>

                </Grid2>

            </Box>

            {/* ── Why Choose MySkill Box (CTA) ── */}
            <Box
                sx={{
                    width: '100%',
                    backgroundImage: `url(${Constants.customImages.SkillBoxHeadingImg})`,
                    backgroundSize: 'contain',
                    backgroundPosition: 'start',
                    backgroundRepeat: 'no-repeat',
                    pt: "7rem"
                }}
            >
                <Box sx={{ position: "relative", width: "100%" }}>
                    <Typography
                        variant="h4"
                        color='red.main'
                        mb={3}
                        fontWeight={600}
                        textAlign={"center"}
                        dangerouslySetInnerHTML={{ __html: ctaSection.quoteText }}
                    />
                    <Box sx={{
                        position: "absolute", width: "36px", height: "36px", borderRadius: "50%",
                        border: "3px solid #FAC584", left: "12%", zIndex: 20
                    }} />
                </Box>
            </Box>

        </Box>
    );
};

export default SkillBox;